import {
  CreatePersonInput,
  PersonFilter,
  UpdatePersonInput,
  UserWithPerson,
} from "../../routeComponents/Persons/Person";
import { PaginationQueryObject, QueryConfig } from "./Queries";

export const createPersonMutation = (input: CreatePersonInput): QueryConfig => {
  const query = `mutation createPerson($input: PersonCreateInput) {
    createPerson(input: $input) {
      contractingCompany {
        id
        name
        customer {
          id
        }
      }
      displayName
      email
      employeeID
      firstName
      id
      lastName
      middleName
      phone
      relationshipToCustomer
      primaryLocation
      supervisor {
        id
      }
      userId
      personSensitive {
        gender
        streetAddress
        address2
        address3
        address4
        city
        zipPostal
        stateProv
        country
        currentPosition
        currentPositionStartDate
        birthDate
        hireDate
        effectiveDate
      }
    }
  }`;

  return {
    query: query,
    variables: { input },
  };
};

// List of Persons
export const getPersonsQuery = (
  filter: PersonFilter,
  pagination: PaginationQueryObject
): QueryConfig => {
  const query = `query getPersonsQuery($filter: PersonFilter, $pagination: PaginationInput) {
    persons(filter: $filter, paginationInput: $pagination) {
      items {
        contractingCompany {
          id
          name
          customer {
            id
          }
        }
        displayName
        email
        employeeID
        firstName
        id
        lastName
        middleName
        phone
        relationshipToCustomer
        supervisor {
          id
        }
        userId
        personSensitive {
          gender
          streetAddress
          address2
          address3
          address4
          city
          zipPostal
          stateProv
          country
          currentPosition
          currentPositionStartDate
          birthDate
          hireDate
          effectiveDate
        }
        primaryLocation
      }
      pageInfo { 
        hasNext 
        hasPrevious 
        pageNumber 
        pageSize 
        totalElements 
        totalPages
      }
    }
  }`;

  return {
    query: query,
    variables: {
      filter: filter,
      pagination: pagination,
    },
  };
};

export const getPersonsGateway = (
  filter: PersonFilter,
  paginationInput: PaginationQueryObject
): QueryConfig => {
  const query = `query getPersons($filter: PersonFilter, $paginationInput: PaginationInput) {
    persons(filter: $filter, paginationInput: $paginationInput){
      items{
        firstName
        lastName
        id
        middleName
        email
        displayName
        active
        userId
        primaryLocation
        user {
          displayedLoginName
        }
      }
      pageInfo{
        pageNumber
        pageSize
        totalPages
        totalElements
        hasNext
        hasPrevious
      }
    }
  }`;

  return {
    query,
    variables: {
      filter,
      paginationInput,
    },
  };
};

export const getRoles = (
  id: string,
  filter: any,
  paginationInput: PaginationQueryObject
): QueryConfig => {
  const query = `query getUser(
    $id: ID
    $filter: RoleAssignmentFilter
    $paginationInput: PaginationInput
  ) {
    user(id: $id) {
      id
      roleAssignments(
        roleAssignmentFilter: $filter
        paginationInput: $paginationInput
      ) {
        pageInfo {
          pageNumber
          pageSize
          totalPages
          totalElements
          hasNext
          hasPrevious
        }
        items {
          id
          role {
            id
            name
            description
          }
          location {
            id
            name
          }
        }
      }
    }
  }`;

  return {
    query,
    variables: {
      id,
      filter,
      paginationInput,
    },
  };
};

export const getSupervisorsQuery = (
  customerId: string,
  pagination: PaginationQueryObject
): QueryConfig => {
  const query = `query getPersonsQuery($filter: PersonFilter, $pagination: PaginationInput) {
    persons(filter: $filter, paginationInput: $pagination) {
      items {
        displayName
        email
        firstName
        id
        lastName
        userId
      }
    }
  }`;

  return {
    query: query,
    variables: {
      filter: {
        and: {
          customerId,
        },
      },
      pagination: pagination,
    },
  };
};

export const getContractingCompaniesQuery = (
  customerId: string,
  pagination: PaginationQueryObject
): QueryConfig => {
  const query = `query getContractingCompaniesQuery($filter: ContractingCompanyFilter, $pagination: PaginationInput) {
    contractingCompanies (filter: $filter, paginationInput: $pagination) {
      items {
          id
          name
          customer {
            id
          }
        }
    }
  }`;

  return {
    query: query,
    variables: {
      filter: {
        and: {
          customerId,
        },
      },
      pagination: pagination,
    },
  };
};

// Single person
export const getPersonQuery = (
  id: string,
  effectiveDate?: string
): QueryConfig => {
  const query = `query getPersonQuery($id: ID, $effectiveDate: Date) {
    person(id: $id, effectiveDate: $effectiveDate) {
      contractingCompany {
        id
        name
        customer {
          id
        }
      }
      user {
        id
        displayedLoginName
        active
        customer{
        id
        customerDomainName
        }
        userDemographic{
        email
        }
      }
      email
      active
      employeeID
      firstName
      id
      lastName
      middleName
      phone
      faxNumber
      yearsInJob
      endDate
      relationshipToCustomer
      supervisor {
        displayName
        email
        firstName
        id
        lastName
        userId
      }
      userId
      displayName
      personSensitive {
        gender
        streetAddress
        address2
        address3
        address4
        city
        zipPostal
        stateProv
        country
        currentPosition
        currentPositionStartDate
        birthDate
        hireDate
        effectiveDate
      }
      primaryLocation
    }
  }`;

  return {
    query: query,
    variables: {
      id,
      effectiveDate,
    },
  };
};

export type UserWithPersonCreateInput = {
  input: UserWithPerson;
};

export const createUserWithPersonMutation = (
  input: CreatePersonInput
): QueryConfig => {
  const query = `mutation createUserWithPerson($input: UserWithPersonCreateInput) {
    createUserWithPerson(input: $input) {
      active
      actualLoginName
      customer {
        id
      }
      displayedLoginName
      id
      person {
        id
        contractingCompany {
          id
          name
          customer {
            id
          }
        }
        displayName
        email
        employeeID
        firstName
        id
        lastName
        middleName
        phone
        faxNumber
        relationshipToCustomer
        supervisor {
          id
        }
        userId
        personSensitive {
          gender
          streetAddress
          address2
          address3
          address4
          city
          zipPostal
          stateProv
          country
          currentPosition
          currentPositionStartDate
          birthDate
          hireDate
          effectiveDate
        }
      }
      userDemographic {
        id
        firstName
        lastName
        middleName
        displayName
        birthDate
        gender
        email
        phone
      }
      userStatus
    }
  }
  `;

  return {
    query: query,
    variables: { input },
  };
};
export const updatePerson = (input: {
  input: UpdatePersonInput;
}): QueryConfig => {
  const query = `mutation updatePerson ($input: PersonUpdateInput) {
    updatePerson(input: $input){
      contractingCompany {
        id
        name
        customer {
          id
        }
      }
      displayName
      email
      employeeID
      firstName
      id
      lastName
      middleName
      phone
      faxNumber
      yearsInJob
      relationshipToCustomer
      supervisor {
        id
      }
      userId
      personSensitive {
        gender
        streetAddress
        address2
        address3
        address4
        city
        zipPostal
        stateProv
        country
        currentPosition
        currentPositionStartDate
        birthDate
        hireDate
        effectiveDate
      }
    }
  }`;

  return {
    query: query,
    variables: input,
  };
};

export const createUserFromPerson = (input: {
  customerId: string;
  personId: string;
  username: string;
  email: string;
}): QueryConfig => {
  const query = `mutation ($input: CreateUserFromPersonInput) {
    createUserFromPerson(input: $input){
      id
      active
      userDemographic {
        email
      }
    }
  }`;

  return {
    query,
    variables: {
      input: input,
    },
  };
};

export const getUserQuery = (
  id: string,
  actualLoginName?: string,
  displayedLoginName?: string
): QueryConfig => {
  const query = `query getUserQuery($id: ID, $actualLoginName: String, $displayedLoginName: String) {
    user(id: $id, actualLoginName: $actualLoginName, displayedLoginName: $displayedLoginName){
      id
      active
      displayedLoginName
      userDemographic {
        email
      }
      customer {
        id
        customerDomainName
      }
    }
  }`;

  return {
    query,
    variables: {
      id,
      actualLoginName,
      displayedLoginName,
    },
  };
};

export const updateUser = ({
  active,
  id,
  username,
}: {
  active?: boolean;
  id: string;
  username?: string;
}) => {
  const query = `mutation updateUser($input: UserUpdateInput) {
    updateUser(input: $input){
      id
      displayedLoginName
      active
       userDemographic {
        email
      }
    }
  }`;

  return {
    query,
    variables: {
      input: {
        active,
        id,
        username,
      },
    },
  };
};

export interface GrantRoleAssignmentsInput {
  customerId: string;
  locationIds: string[];
  roleId: string;
  userId: string;
}

export const grantRoleAssigmentsMutation = (
  inputs: GrantRoleAssignmentsInput[]
) => {
  const query = `mutation ($inputs: [GrantRoleToUserInput]!) {
    grantRoleAssignments(inputs: $inputs)
  }`;

  return {
    query,
    variables: {
      inputs,
    },
  };
};

export interface RevokeRoleAssignmentsInput {
  customerId: string;
  locationIds: string[];
  roleId: string;
  userId: string;
}

export const revokeRoleAssigmentsMutation = (
  inputs: RevokeRoleAssignmentsInput[]
) => {
  const query = `mutation ($inputs: [RevokeRoleAssignmentInput]!) {
    revokeRoleAssignments(inputs: $inputs)
  }`;

  return {
    query,
    variables: {
      inputs,
    },
  };
};

export const getPersonIdQuery = (userId: string): QueryConfig => {
  const query = `query getUser($id: ID) {
    user(id: $id) {
      person {
        id
      }
    }
  }`;

  return {
    query,
    variables: {
      id: userId,
    },
  };
};
